import _ from "lodash";
import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { UUIDString } from "../../../../common/service/server/Types";
import { GetCompliantPageWrapper } from "../../../components/layout/GetCompliantPageWrapper";
import { PageHeader } from "../../../components/layout/header/Header";
import { IncludeInformationCollectedForOtherBusinessesCallout } from "../../../components/organisms/callouts/RegulationCallouts";
import { ConsumerDataCollectionForm } from "../../../components/organisms/collection-groups/ConsumerDataCollectionForm";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../../hooks";
import {
  useCollectionGroup,
  useNonEmploymentCollectionGroups,
} from "../../../hooks/useCollectionGroups";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import { useProgressKeys } from "../../../hooks/useProgressKeys";
import { useGetCompliantContext } from "../../../root/GetCompliantContext";
import { Routes } from "../../../root/routes";
import { consumerCollectionSurveyName } from "../../../surveys/consumerCollectionCategoriesSurvey";

import {
  BUSINESS_SURVEY_NAME,
  COLLECTION_FOR_OTHER_BUSINESSES_SLUG,
} from "../../../surveys/businessSurvey";
import { GetCompliantFooter } from "../ProgressFooter";
import { Loading } from "../../../components";
import { SENSITIVE_DATA_SURVEY_NAME } from "../../../surveys/sensitiveDataSurvey";
import { PriorityHigh } from "@mui/icons-material";

export const ConsumerDataCollectionForGroup: React.FC = () => {
  const { progress } = useGetCompliantContext();

  const { groupId } = useParams<{ groupId: UUIDString }>();
  const [org] = usePrimaryOrganization();
  const orgId = usePrimaryOrganizationId();

  const [groups, groupsRequest] = useNonEmploymentCollectionGroups(orgId);
  const { data: dataMap, request: dataMapRequest } = useDataMap(orgId);
  const [group, groupRequest] = useCollectionGroup(orgId, groupId);

  const {
    answers: businessSurvey,
    request: bizSurveyRequest,
    invalidate: invalidateBusinessSurvey,
  } = useSurvey(orgId, BUSINESS_SURVEY_NAME);

  const { invalidate: invalidateSensitiveDataSurvey } = useSurvey(
    orgId,
    SENSITIVE_DATA_SURVEY_NAME,
  );

  useEffect(() => {
    return () => {
      invalidateBusinessSurvey();
      invalidateSensitiveDataSurvey();
    };
  }, []);

  const isOnlyGroup = !_.isNil(groups) && groups?.length < 2;

  const {
    answers,
    setAnswer,
    request: surveyRequest,
    mutators: { patchAnswer },
  } = useSurvey(orgId, group ? consumerCollectionSurveyName(group.id) : undefined);

  const keys = useMemo(() => groups?.map((g) => consumerCollectionSurveyName(g.id)), [groups]);
  const { update: updateProgress } = useProgressKeys(orgId, keys);

  const handleNext = async () => {
    const key = consumerCollectionSurveyName(group?.id);
    await updateProgress({ keyId: key, progress: "DONE" });
  };

  const nextUrl = isOnlyGroup
    ? undefined
    : Routes.getCompliant.businessSurvey.consumerDataCollection;
  const prevUrl = isOnlyGroup
    ? undefined
    : Routes.getCompliant.businessSurvey.consumerDataCollection;

  const nextLabel = "Next";
  const noProgress = isOnlyGroup ? false : true;

  const hasFinishedSurvey = Boolean(answers["inferences-done"]);

  if (!group) {
    return <Loading />;
  }

  return (
    <GetCompliantPageWrapper
      requests={[groupsRequest, surveyRequest, dataMapRequest, groupRequest, bizSurveyRequest]}
      currentProgress={progress}
      progressFooter={
        <GetCompliantFooter
          presentStep="BusinessSurvey.DataCollection"
          nextLabel={nextLabel}
          nextUrl={nextUrl}
          saveRequests={[patchAnswer]}
          nextOnClick={handleNext}
          nextDisabled={!hasFinishedSurvey}
          prevUrl={prevUrl}
          noProgress={noProgress}
        />
      }
    >
      <PageHeader titleContent="Collection" />

      <h4 className="intermediate">
        What information do you collect about {group?.name ?? "this group"}?
      </h4>

      <div className="my-md">
        <p className="flex-row flex--align-center">
          <PriorityHigh color="error" fontSize="small" />
          Don't include data collected from employees. We'll cover HR collection in a later step.
        </p>
      </div>

      {businessSurvey[COLLECTION_FOR_OTHER_BUSINESSES_SLUG] == "true" && (
        <div className="my-md">
          <IncludeInformationCollectedForOtherBusinessesCallout />
        </div>
      )}

      <ConsumerDataCollectionForm
        answers={answers}
        setAnswer={setAnswer}
        dataMap={dataMap}
        org={org}
        collectionGroup={group}
      />
    </GetCompliantPageWrapper>
  );
};
